// eslint-disable-next-line @typescript-eslint/no-unused-vars
import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import IconArrowFirst from '../../Icons/IconArrowFirst';
import IconArrowLast from '../../Icons/IconArrowLast';
import ExpandedRow from './ExpandedTable';
import { useAuthStore } from '../../store/AuthContext';
import NoData from '../../components/NoData';

const PAGE_SIZE = 10;

type Props = {
  data: DataCompra[];
  total: number;
  withPagination: boolean;
  isTicketDownloadable: boolean;
};

interface DataCompra {
  fecha: string;
  concepto: string;
  importe: number;
  ahorroTicket: number;
  idTicket: number;
  ingresoEroskiClub: number;
  ingresoEroskiClubOro: number;
  otrosDescuentos: number;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const CustomPaginationTable: FC<Props> = ({ data, isTicketDownloadable }) => {
  const dataUser = useAuthStore();
  const { moneda } = dataUser.user;
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [expandedRowId, setExpandedRowId] = useState<number | null>(null);
  const pageCount = Math.max(1, Math.ceil(data.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount);
  const firstRowIndex = (currentPage - 1) * PAGE_SIZE;
  const visibleData = data.slice(firstRowIndex, firstRowIndex + PAGE_SIZE);

  const isExpandableRow = (row: DataCompra) => {
    return Boolean(row);
  };

  const expandRowComponent = (row: DataCompra) => {
    return (
      <ExpandedRow data={row} isTicketDownloadable={isTicketDownloadable} />
    );
  };

  const toggleExpandedRow = (row: DataCompra) => {
    if (!isExpandableRow(row)) {
      return;
    }

    setExpandedRowId((currentRowId) => {
      return currentRowId === row.idTicket ? null : row.idTicket;
    });
  };

  const getPageNumbers = () => {
    if (pageCount < 5) {
      return Array.from({ length: pageCount }, (_, index) => index + 1);
    }

    let start = currentPage - 2;
    let end = currentPage + 2;

    if (start < 1) {
      start = 1;
      end = 5;
    }

    if (end > pageCount) {
      end = pageCount;
      start = pageCount - 4;
    }

    return Array.from({ length: end - start + 1 }, (_, index) => start + index);
  };

  return (
    <>
      {/* Elemento oculto FirstPage para clonar y poder añadirlo al paginado */}
      <li id="first-page" className="page-item hidden">
        {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
        <a href="" className="page-link">
          <div className="icon icon-medium go-back">
            <IconArrowFirst />
          </div>
        </a>
      </li>

      {/* Elemento oculto LastPage para clonar y poder añadirlo al paginado */}
      <li id="last-page" className="page-item hidden">
        {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
        <a href="" className="page-link">
          <div className="icon icon-medium">
            <IconArrowLast />
          </div>
        </a>
      </li>
      {(() => {
        if (data !== null && data.length > 0) {
          return (
            <>
              <div className="table-responsive">
                <table className="table compras-table">
                  <caption className="sr-only">{t('compras.title')}</caption>
                  <thead>
                    <tr>
                      <th className="th-fecha-compras" scope="col">
                        {t('compras.date')}
                      </th>
                      <th className="" scope="col">
                        {t('compras.concept')}
                      </th>
                      <th className="th-importe-compras" scope="col">
                        {t('compras.amount')}
                      </th>
                      <th className="th-puntos-compras" scope="col">
                        {t('compras.savedTickets')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleData.map((movement, index) => (
                      <>
                        <tr
                          key={`compras-movement-${firstRowIndex + index}`}
                          className={`tr-static row-${movement.idTicket}${
                            expandedRowId === movement.idTicket ? ' activa' : ''
                          }`}
                          onClick={() => toggleExpandedRow(movement)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault();
                              toggleExpandedRow(movement);
                            }
                          }}
                        >
                          <td className="td-fecha-compras">{movement.fecha}</td>
                          <td>{movement.concepto}</td>
                          <td className="td-importe-compras">
                            {`${movement.importe}${
                              moneda === 'euro' ? t('euro') : t('cc')
                            }`}
                          </td>
                          <td className="td-puntos-compras">
                            {`${movement.ahorroTicket}${
                              moneda === 'euro' ? t('euro') : t('cc')
                            }`}
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              viewBox="0 0 24 15"
                              className="row-expand-icon"
                            >
                              <polygon
                                fillRule="evenodd"
                                points="2.832 5 12 14.216 21.168 5 24 7.832 12 19.832 0 7.832"
                                transform="translate(0 -5)"
                              />
                            </svg>
                          </td>
                        </tr>
                        {expandedRowId === movement.idTicket && (
                          <tr className="detalle-fila">
                            {expandRowComponent(movement)}
                          </tr>
                        )}
                      </>
                    ))}
                  </tbody>
                </table>
              </div>
              {pageCount > 1 && (
                <nav aria-label={t('compras.pagination')}>
                  <ul className="pagination">
                    <li
                      className={`page-item ${
                        currentPage === 1 ? 'disabled' : ''
                      }`}
                    >
                      <button
                        type="button"
                        className="page-link"
                        disabled={currentPage === 1}
                        onClick={() => setPage(currentPage - 1)}
                      >
                        {t('compras.pagination-previous')}
                      </button>
                    </li>
                    {getPageNumbers().map((pageNumber) => (
                      <li
                        className={`page-item ${
                          pageNumber === currentPage ? 'active' : ''
                        }`}
                        key={pageNumber}
                      >
                        <button
                          type="button"
                          className="page-link"
                          aria-current={
                            pageNumber === currentPage ? 'page' : undefined
                          }
                          onClick={() => setPage(pageNumber)}
                        >
                          {pageNumber}
                        </button>
                      </li>
                    ))}
                    <li
                      className={`page-item ${
                        currentPage === pageCount ? 'disabled' : ''
                      }`}
                    >
                      <button
                        type="button"
                        className="page-link"
                        disabled={currentPage === pageCount}
                        onClick={() => setPage(currentPage + 1)}
                      >
                        {t('compras.pagination-next')}
                      </button>
                    </li>
                  </ul>
                </nav>
              )}
            </>
          );
        }
        return (
          <NoData>
            <div className="mensaje">
              <p>{t('compras.noRecords')}</p>
            </div>
          </NoData>
        );
      })()}
    </>
  );
};

export default CustomPaginationTable;
